// Constants for the Plate → HTML serializer (plateToHtml.ts).
//
// Node-type and listStyleType coverage mirrors docxExport.ts so the two
// exporters stay in lockstep. Mention markup mirrors the legacy
// slateToHtml output (src/common/TextEditor/utils/SlateSerialisationUtil.js)
// that the email backend / comment preview already understand.

// Element node types emitted by the Plate editor (see usePlateEditorLogic.tsx).
export const PLATE_NODE_TYPES = {
    TOC: 'toc',
    PARAGRAPH: 'p',
    H1: 'h1',
    H2: 'h2',
    H3: 'h3',
    BLOCKQUOTE: 'blockquote',
    HR: 'hr',
    CALLOUT: 'callout',
    IMAGE: 'image',
    LINK: 'a',
    MENTION: 'mention',
    TABLE: 'table',
    TABLE_ROW: 'tr',
    TABLE_CELL: 'td',
    TABLE_CELL_HEADER: 'th',
    COLUMN_GROUP: 'column_group',
    COLUMN: 'column',
    CODE_BLOCK: 'code_block',
    CODE_LINE: 'code_line',
} as const;

// Synthetic list element types injected by the list-reconstruction pre-pass.
// Plate's data model is indent-based (a list item is a <p> carrying
// listStyleType + indent — see components/BlockList/BlockList.tsx). The
// serializer wraps consecutive list <p> nodes into these real ul/ol/li
// element nodes so PlateStatic emits proper <ul>/<ol><li> markup.
export const LIST_NODE_TYPES = {
    UL: 'serializer-ul',
    OL: 'serializer-ol',
    LI: 'serializer-li',
} as const;

// listStyleType values that map to an ordered list (<ol>). Anything else with
// a listStyleType is treated as a bulleted list (<ul>). Mirrors the ordered
// set in components/BlockList/BlockList.tsx and docxExport.ts isOrderedList.
export const ORDERED_LIST_STYLES: readonly string[] = [
    'decimal',
    'decimal-leading-zero',
    'lower-alpha',
    'upper-alpha',
    'lower-roman',
    'upper-roman',
];

// CSS class + data attribute for a mention chip. Must match the legacy
// slateToHtml markup the email backend/preview already parse.
export const MENTION_CLASS = 'text-editor-mention';
export const MENTION_DATA_ATTR = 'data-mention-user-id';

// Class on the static editor root wrapper that serializeHtml emits; the
// cleanup pass unwraps it.
export const SLATE_EDITOR_ROOT_CLASS = 'slate-editor';

// Slate sentinel attributes/classes that leak through serializeHtml and must
// be scrubbed for clean, portable email/comment HTML.
export const SLATE_DATA_ATTR_PREFIX = 'data-slate';
export const SLATE_CLASS_PREFIX = 'slate-';

// serializeHtml wraps text leaves in <span> with position:relative; this is
// the only inline style we strip wholesale.
export const SLATE_RELATIVE_STYLE = 'position:relative';

// Zero-width / BOM characters Plate injects around void inline nodes
// (mentions): U+FEFF (BOM) and U+200B (zero-width space). Stripped from
// the final HTML so they don't pollute email/comment bodies.
export const ZERO_WIDTH_CHARS_REGEX = /[﻿​]/g;
